import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { AdminPaymentProcessor, AdminAnnualLicenseConfig } from '@/mocks/adminPaymentProcessors';
import {
  adminPaymentProcessors as mockProcessors,
  adminDefaultAnnualLicenseConfig,
} from '@/mocks/adminPaymentProcessors';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<AdminPaymentProcessor[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.96) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve([...mockProcessors].sort((a, b) => a.order - b.order));
    }, 600 + Math.random() * 250);
  });
}

export default function AdminPaymentProcessors() {
  const [processors, setProcessors] = useState<AdminPaymentProcessor[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [licenseConfig] = useState<AdminAnnualLicenseConfig>(adminDefaultAnnualLicenseConfig);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((d) => { setProcessors(d); setLoadingState('ready'); })
      .catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTestConnection = (proc: AdminPaymentProcessor) => {
    if (proc.category === 'manual') {
      SwalAlert.warning('Método manual', 'Los métodos manuales no requieren prueba de conexión.');
      return;
    }
    if (!proc.enabled) {
      SwalAlert.warning('Procesador inactivo', 'Este procesador no está habilitado por la plataforma.');
      return;
    }

    setTestingId(proc.id);
    setTimeout(() => {
      const success = Math.random() > 0.3;
      setTestingId(null);
      if (success) {
        setProcessors((prev) =>
          prev.map((p) =>
            p.id === proc.id
              ? { ...p, lastTest: new Date().toISOString().slice(0, 16).replace('T', ' ') }
              : p,
          ),
        );
        SwalAlert.toast(`${proc.name} responde correctamente`, 'success');
      } else {
        SwalAlert.error('Error de conexión', `${proc.name} no respondió. Contactá al administrador de la plataforma.`);
      }
    }, 2000);
  };

  const activeCount = processors.filter((p) => p.enabled).length;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">

        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/admin/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Métodos de Pago</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Métodos de Pago</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${activeCount} métodos disponibles para cobrar · Configurados por la plataforma`
                    : 'Cargando métodos de pago...'}
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                <i className="ri-information-line text-[9px]"></i>Solo lectura
              </span>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="animate-pulse p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 py-2">
                      <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                      <div className="flex-1 space-y-1.5">
                        <div className="w-24 h-3 bg-background-200 rounded"></div>
                        <div className="w-16 h-2 bg-background-200 rounded"></div>
                      </div>
                      <div className="w-20 h-6 bg-background-200 rounded-full"></div>
                      <div className="w-16 h-3 bg-background-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>Reintentar
                </button>
              </div>
            )}

            {/* Annual License Config Card — read only */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                    <i className="ri-award-line text-emerald-600 text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-foreground-900 font-bold text-sm">{licenseConfig.title}</h3>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold whitespace-nowrap">
                        {licenseConfig.badge}
                      </span>
                    </div>
                    <p className="text-foreground-500 text-[10px] leading-relaxed">{licenseConfig.description}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <span className="text-foreground-900 text-lg font-black">${licenseConfig.price}</span>
                      <span className="text-foreground-400 text-[10px]">{licenseConfig.priceLabel}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {licenseConfig.features.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">
                          <i className="ri-check-line text-emerald-500 text-[9px]"></i>{f}
                        </span>
                      ))}
                    </div>
                    <p className="text-foreground-400 text-[9px] mt-3">{licenseConfig.note}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-100/50">
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Procesador</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Modo</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Se usa para</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Comisión</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Última prueba</th>
                        <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {processors.map((proc) => {
                        const commissionDisplay =
                          proc.commissionPercent > 0 || proc.commissionFixed > 0
                            ? `${proc.commissionPercent}% + $${proc.commissionFixed.toFixed(2)}`
                            : 'Sin comisión';

                        return (
                          <tr
                            key={proc.id}
                            className={`hover:bg-background-100/60 transition-colors ${!proc.enabled ? 'opacity-50' : ''}`}
                          >
                            {/* Processor */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${proc.logoColor}`}>
                                  <i className={`${proc.icon} text-sm`}></i>
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-foreground-800 text-xs font-semibold truncate">{proc.name}</p>
                                    {proc.isDefault && (
                                      <span className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-600 text-[8px] font-bold whitespace-nowrap">
                                        Default
                                      </span>
                                    )}
                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium whitespace-nowrap ${proc.category === 'api' ? 'bg-sky-50 text-sky-600' : 'bg-amber-50 text-amber-600'}`}>
                                      {proc.category === 'api' ? 'API' : 'Manual'}
                                    </span>
                                  </div>
                                  {!proc.enabled && (
                                    <p className="text-foreground-400 text-[9px] mt-0.5">No habilitado por la plataforma</p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${proc.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-background-200 text-foreground-400'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${proc.enabled ? 'bg-emerald-500' : 'bg-foreground-300'}`}></span>
                                {proc.enabled ? 'Disponible' : 'No disponible'}
                              </span>
                            </td>

                            {/* Mode */}
                            <td className="px-4 py-3">
                              {proc.category === 'api' ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${proc.testMode ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                  <i className={`${proc.testMode ? 'ri-flask-line' : 'ri-rocket-line'} text-[9px]`}></i>
                                  {proc.testMode ? 'Sandbox' : 'Producción'}
                                </span>
                              ) : (
                                <span className="text-foreground-300 text-[10px]">—</span>
                              )}
                            </td>

                            {/* Usage */}
                            <td className="px-4 py-3">
                              {proc.usageFunctions ? (
                                <div className="flex flex-wrap gap-1 max-w-[220px]">
                                  {proc.usageFunctions.filter((f) => f.enabled).slice(0, 3).map((fn) => (
                                    <span key={fn.id} className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-emerald-50 text-emerald-600 whitespace-nowrap">
                                      {fn.label}
                                    </span>
                                  ))}
                                  {proc.usageFunctions.filter((f) => f.enabled).length > 3 && (
                                    <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-background-200 text-foreground-400 whitespace-nowrap">
                                      +{proc.usageFunctions.filter((f) => f.enabled).length - 3} más
                                    </span>
                                  )}
                                  {proc.usageFunctions.filter((f) => f.enabled).length === 0 && (
                                    <span className="text-foreground-300 text-[9px]">—</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-foreground-300 text-[9px]">—</span>
                              )}
                            </td>

                            {/* Commission */}
                            <td className="px-4 py-3">
                              <span className="text-foreground-800 text-xs font-mono font-semibold">{commissionDisplay}</span>
                            </td>

                            {/* Last test */}
                            <td className="px-4 py-3">
                              <span className="text-foreground-500 text-[10px] whitespace-nowrap">
                                {proc.lastTest || '—'}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 justify-end">
                                <button
                                  onClick={() => handleTestConnection(proc)}
                                  disabled={testingId === proc.id || !proc.enabled || proc.category === 'manual'}
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${!proc.enabled || proc.category === 'manual' ? 'bg-background-100 text-foreground-300 cursor-default' : 'bg-sky-50 text-sky-500 hover:bg-sky-100'}`}
                                  title={proc.category === 'manual' ? 'Método manual' : !proc.enabled ? 'No disponible' : 'Probar conexión'}
                                >
                                  {testingId === proc.id ? (
                                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                                  ) : (
                                    <i className="ri-plug-line text-xs"></i>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Empty state */}
                {processors.length === 0 && (
                  <div className="p-12 text-center">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-bank-card-line text-foreground-300 text-xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm font-semibold">Sin métodos de pago</p>
                    <p className="text-foreground-400 text-xs mt-1">La plataforma no tiene métodos de pago habilitados.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile footer */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Métodos de Pago</p>
      </div>
    </div>
  );
}